/**
 * Google Tag Manager Block
 *
 * Injection block that loads the GTM container script.
 * Site-wide: same container_id across all pages.
 *
 * By default this block ALSO subscribes to the Vibesite event bus and pushes
 * each canonical event into `window.dataLayer` as
 *   { event: 'vibesite.<canonical_name>', ...payload }
 * so customers using the Custom Event trigger pattern in GTM can react to
 * Vibesite events with zero extra setup.
 *
 * The `vibesite.` namespace prevents collision with any custom event names
 * the customer already has configured. Default-on is purely additive —
 * Click/URL trigger setups and existing Custom Event triggers remain
 * unaffected. Customers who don't want bus events surfaced in their
 * dataLayer can opt out via the `push_vibesite_events_to_datalayer` token.
 */

import { useEffect, useRef } from 'react';
import type { BlockDefinition, BlockProps } from '../../types';
import type { ComponentContentSchema } from '@vibesites/runtime';
import {
  useBlockToken,
  useVibesiteEvents,
} from '@vibesites/runtime/react';
import { Event, type VibesiteEventName } from '@vibesites/runtime';
import { useTrackingAllowed } from '../../shared/analytics';

const SCRIPT_ID = 'gtm-script';
const NAMESPACE = 'vibesite.';

function GoogleTagManagerComponent(_props: BlockProps) {
  const containerId = useBlockToken('container_id', { defaultValue: '' });
  const pushEventsToken = useBlockToken('push_vibesite_events_to_datalayer', {
    defaultValue: 'true',
  });
  const pushEnabled = pushEventsToken !== 'false';

  const { subscribe } = useVibesiteEvents();
  // GTM is classified as 'advertising' because a GTM container can load
  // arbitrary ad tags (Meta, Google Ads, TikTok, etc.) that a visitor
  // opting out via CCPA/CPRA expects to be blocked.
  const trackingAllowed = useTrackingAllowed('advertising');
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    if (!trackingAllowed || !containerId) return;

    if (document.getElementById(SCRIPT_ID)) return;

    // GTM snippet
    const script = document.createElement('script');
    script.id = SCRIPT_ID;
    script.textContent = `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);})(window,document,'script','dataLayer','${containerId}');`;
    document.head.appendChild(script);

    // noscript fallback
    const noscript = document.createElement('noscript');
    const iframe = document.createElement('iframe');
    iframe.src = `https://www.googletagmanager.com/ns.html?id=${containerId}`;
    iframe.title = 'Google Tag Manager';
    iframe.height = '0';
    iframe.width = '0';
    iframe.style.cssText = 'display:none;visibility:hidden';
    noscript.appendChild(iframe);
    document.body.insertBefore(noscript, document.body.firstChild);

    return () => {
      mountedRef.current = false;
    };
  }, [trackingAllowed, containerId]);

  // Bridge bus events into dataLayer under the vibesite.* namespace.
  // No-op when GTM isn't configured or the customer opted out.
  useEffect(() => {
    if (!trackingAllowed || !containerId || !pushEnabled) return;

    const off = subscribe('*', (name, payload) => {
      const w = window as unknown as { dataLayer?: unknown[] };
      if (!Array.isArray(w.dataLayer)) return;
      w.dataLayer.push({
        event: `${NAMESPACE}${name}`,
        ...(payload as Record<string, unknown>),
      });
    });

    return off;
  }, [trackingAllowed, containerId, pushEnabled, subscribe]);

  return null;
}

const contentSchema: ComponentContentSchema = {
  slot: 'GoogleTagManager',
  displayName: 'Google Tag Manager',
  tokens: {
    container_id: {
      label: 'Container ID',
      type: 'text',
      defaultField: 'custom_google_gtm_id',
      suggestedFields: ['custom_google_gtm_id'],
      description: 'GTM container ID (e.g. GTM-XXXXXXX).',
      required: true,
    },
    push_vibesite_events_to_datalayer: {
      label: 'Push Vibesite events to dataLayer',
      type: 'text',
      defaultValue: 'true',
      description:
        'When "true" (default), bridge canonical Vibesite events into ' +
        'window.dataLayer as { event: "vibesite.<name>", ...payload } so ' +
        'GTM Custom Event triggers can react to them. Set to "false" to ' +
        'load GTM only without surfacing Vibesite events in dataLayer.',
    },
  },
};

export const googleTagManagerBlock: BlockDefinition = {
  id: 'google-tag-manager',
  name: 'Google Tag Manager',
  description:
    'Google Tag Manager container. Optionally bridges Vibesite canonical events into dataLayer under the vibesite.* namespace.',
  category: 'Analytics',
  placement: 'injection',
  defaultScope: 'global',
  component: GoogleTagManagerComponent,
  contentSchema,
  tags: ['gtm', 'google', 'tag', 'manager', 'analytics', 'marketing'],
  trackedEvents: (Object.keys(Event) as VibesiteEventName[]).map((name) => ({
    event: name,
    mapsTo: `dataLayer.push({ event: '${NAMESPACE}${name}' })`,
  })),
};
